/**
 * Side effects after a sales order is successfully created on the API:
 * project link, customer email (with PDF), Stripe URL prep, notifications.
 * Kept separate from SignageCreateOrder.tsx so the page file stays readable.
 * Runs in the background (caller should not await) so navigation can be immediate.
 */
import { safeConsole } from "utils/envUtils";
import { sendSalesOrderTriggerEmail } from "utils/salesOrderTriggerEmailEngine";
import { linkProjectQuote } from "components/proejctsandquoteapicalls";

/** Narrow form fields this module reads (same as Formik values usage in original inline code). */
export type SignageCreateOrderPostCreateFormValues = {
  notes?: string;
  userEmail?: string;
  teamMemberId?: string;
  userFirstName?: string;
};

export type SignageCreateOrderPostCreateContext = {
  result: any;
  op: Record<string, any>;
  token: string;
  linkedProjectId: string | null;
  projectIdFromQuery: string;
  /** True when user cleared Project card before save — skip link POST. */
  userClearedProject?: boolean;
  finalShippingAddress: any;
  finalBillingAddress: any;
  agent_name: any;
  products: any;
  grandTotalNow: number;
  grandTotal: number;
  /** Formik values (only a subset of fields is used; kept loose to avoid coupling to the full form type). */
  values: any;
  isSignageTeammember: boolean;
  parentSignageName: string | null | undefined;
  userName: string | null | undefined;
  userId: string | null | undefined;
  baseRoute: string;
  selectedOfficeAgent: any;
  selectedOffice: any;
  selectedTeamMemberOption: any;
  selectedAgent: any;
  selectedBrokerageOption: any;
  effectiveCouponCreatedById: string | null | undefined;
};

export async function runSignageCreateOrderPostCreateEffects(
  ctx: SignageCreateOrderPostCreateContext
): Promise<void> {
  const {
    result,
    op,
    token,
    linkedProjectId,
    projectIdFromQuery,
    userClearedProject,
    finalShippingAddress,
    finalBillingAddress,
    agent_name,
    products,
    grandTotalNow,
    grandTotal,
    values,
    isSignageTeammember,
    parentSignageName,
    userName,
    userId,
  } = ctx;

  // If this order is created from a project context, link quote to project.
  // Project can come from URL (?project_id=) or from the Project card (linkedProjectId).
  const createdOrderId = String(result?.id || result?._id || result?.order_id || "").trim();
  let createdOrderNumber = String(result?.order_number || "").trim();
  const effectiveProjectIdForLink = userClearedProject
    ? ""
    : String(linkedProjectId || projectIdFromQuery || "").trim();
  if (effectiveProjectIdForLink && token && createdOrderId) {
    try {
      if (!createdOrderNumber) {
        const orderRes = await fetch(`${process.env.REACT_APP_API_URL}/orders/${createdOrderId}`, {
          method: "GET",
          headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        });
        if (orderRes.ok) {
          const orderData = await orderRes.json();
          createdOrderNumber = String(orderData?.order_number || "").trim();
        }
      }
      await linkProjectQuote(token, effectiveProjectIdForLink, {
        linked_order_id: createdOrderId,
        linked_order_number: createdOrderNumber || createdOrderId,
        linked_order_type: "sales_order",
      });
    } catch (err) {
      safeConsole.warn("Failed to link created sales order to project:", err);
    }
  }

  // Send "Sales order" emails after successful order creation
  try {
    safeConsole.log("Order created successfully:", result);
    const actualOrderId = String(result?.id || result?._id || result?.order_id || "").trim();
    let signageCompanyName =
      isSignageTeammember && parentSignageName ? parentSignageName : userName || "Signage Company";

    if (actualOrderId && token) {
      try {
        const orderRes = await fetch(`${process.env.REACT_APP_API_URL}/orders/${actualOrderId}`, {
          method: "GET",
          headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        });
        if (orderRes.ok) {
          const orderDataFromGet = await orderRes.json();
          if (!signageCompanyName && orderDataFromGet?.signage_name) {
            signageCompanyName = String(orderDataFromGet.signage_name);
          }
        }
      } catch (err) {
        safeConsole.warn("Failed to fetch order details for order_number:", err);
      }
    }

    const resolvedSignageId = String(
      result?.signage_id || op?.signage_id || userId || ""
    ).trim();

    // if (actualOrderId && resolvedSignageId && token) {
    //   await sendSalesOrderTriggerEmail({
    //     triggerName: "Signage Sales order",
    //     orderId: actualOrderId,
    //     signageId: resolvedSignageId,
    //     token,
    //     fallbackUserName: String(values?.userFirstName || "").trim() || undefined,
    //     fallbackCompanyName: signageCompanyName,
    //     includeOrderPdfAttachment: true,
    //   });
    // }
  } catch (emailError) {
    safeConsole.warn("Failed to send order creation emails:", emailError);
  }
}
